/**
 * Capacity + queue helpers (F-03, F-17, F-18, F-19b).
 *
 * The rules that make these numbers mean something:
 *  - a project assigned to a person counts that person's load in FULL project
 *    hours (it is an overload signal, not a split of the estimate);
 *  - a team's capacity sums hours over UNIQUE projects of its members — two
 *    assignees from the same team on one project never double-count;
 *  - counts and hours are always broken down by status.
 */

import { PROJECT_STATUSES, priorityRank } from '../api/types';
import type { Assignment, Person, Project, ProjectStatus } from '../api/types';
import { displayDateToTimestamp } from './date';

/** Statuses whose (past) due date is not a problem anymore. */
const OVERDUE_EXEMPT: readonly ProjectStatus[] = ['done', 'rejected', 'deferred'];

export function emptyByStatus(): Record<ProjectStatus, number> {
  return PROJECT_STATUSES.reduce(
    (acc, status) => ({ ...acc, [status]: 0 }),
    {} as Record<ProjectStatus, number>,
  );
}

export interface StatusReport {
  total: number;
  byStatus: Record<ProjectStatus, number>;
  /** Hours summed once per project (never per assignment). */
  hours: number;
  hoursByStatus: Record<ProjectStatus, number>;
  /** Due date in the past on a still-relevant status. */
  overdue: number;
  atRisk: number;
}

export function summarizeProjects(projects: readonly Project[]): StatusReport {
  const report: StatusReport = {
    total: 0,
    byStatus: emptyByStatus(),
    hours: 0,
    hoursByStatus: emptyByStatus(),
    overdue: 0,
    atRisk: 0,
  };
  for (const project of projects) {
    report.total += 1;
    // Statuses are a managed dictionary (F-01): a custom status has no
    // pre-built bucket — one is created on demand, the built-in chips
    // (`in_work`, `agreed`, …) keep their semantic keys either way.
    report.byStatus[project.status] = (report.byStatus[project.status] ?? 0) + 1;
    const hours = project.hours ?? 0;
    report.hours += hours;
    report.hoursByStatus[project.status] = (report.hoursByStatus[project.status] ?? 0) + hours;
    if (isOverdue(project)) report.overdue += 1;
    if (project.atRisk) report.atRisk += 1;
  }
  return report;
}

const PRIORITIES_FALLBACK_RANK = 4;

/**
 * Overdue (F-13 «Просрочено»): a due date in the past on a project that is
 * still relevant — `done`, `rejected` and `deferred` are exempt (a shelved
 * project is not asking for attention).
 */
export function isOverdue(project: Project, today?: string): boolean {
  if (!project.dueDate) return false;
  if (OVERDUE_EXEMPT.includes(project.status)) return false;
  const due = displayDateToTimestamp(project.dueDate);
  if (due === null) return false;
  const now = today ? (displayDateToTimestamp(today) ?? Date.now()) : Date.now();
  return due < now;
}

/** Projects a person is assigned to. */
export function projectsOfPerson(personId: string, projects: readonly Project[]): Project[] {
  return projects.filter((project) => project.assignments.some((a) => a.personId === personId));
}

/** F-18: the person's queue — personal order, then earliest due date, then title. */
export function sortPersonQueue(
  personId: string,
  projects: readonly Project[],
): Array<Project & { assignment: Assignment }> {
  return projectsOfPerson(personId, projects)
    .map((project) => ({
      ...project,
      assignment: project.assignments.find((a) => a.personId === personId) as Assignment,
    }))
    .sort((left, right) => {
      const byRank = priorityRank(left.assignment.personPriority) - priorityRank(right.assignment.personPriority);
      if (byRank !== 0) return byRank;
      const byDue = compareDueDate(left.dueDate, right.dueDate);
      if (byDue !== 0) return byDue;
      return left.title.localeCompare(right.title, 'ru');
    });
}

/** Projects of a team = projects assigned to at least one of its members. */
export function projectsOfTeam(teamId: string, projects: readonly Project[], people: readonly Person[]): Project[] {
  const memberIds = new Set(people.filter((person) => person.teamId === teamId).map((p) => p.id));
  return projects.filter((project) => project.assignments.some((a) => memberIds.has(a.personId)));
}

/**
 * F-19b: the team's queue — the MINIMAL teamPriority rank among the team's own
 * assignments decides a project's place; then due date, then title.
 */
export function sortTeamQueue(
  teamId: string,
  projects: readonly Project[],
  people: readonly Person[],
): Array<Project & { teamPriorityRank: number }> {
  const memberIds = new Set(people.filter((person) => person.teamId === teamId).map((p) => p.id));
  const teamProjects = projectsOfTeam(teamId, projects, people);
  const bestRank = new Map<string, number>();
  for (const project of teamProjects) {
    let rank = Number.MAX_SAFE_INTEGER;
    for (const assignment of project.assignments) {
      if (!memberIds.has(assignment.personId)) continue;
      rank = Math.min(rank, priorityRank(assignment.teamPriority));
    }
    if (rank === Number.MAX_SAFE_INTEGER) rank = PRIORITIES_FALLBACK_RANK;
    bestRank.set(project.id, rank);
  }
  return teamProjects
    .map((project) => ({ ...project, teamPriorityRank: bestRank.get(project.id) ?? PRIORITIES_FALLBACK_RANK }))
    .sort((left, right) => {
      const byRank = left.teamPriorityRank - right.teamPriorityRank;
      if (byRank !== 0) return byRank;
      const byDue = compareDueDate(left.dueDate, right.dueDate);
      if (byDue !== 0) return byDue;
      return left.title.localeCompare(right.title, 'ru');
    });
}

/** Ascending due date, missing dates last. */
export function compareDueDate(left: string | null, right: string | null): number {
  const a = displayDateToTimestamp(left);
  const b = displayDateToTimestamp(right);
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

/** F-03: full project hours per assignment, split by status. */
export function personCapacity(personId: string, projects: readonly Project[]): StatusReport {
  return summarizeProjects(projectsOfPerson(personId, projects));
}

/** F-03: unique projects of the team's members — no double counting. */
export function teamCapacity(
  teamId: string,
  projects: readonly Project[],
  people: readonly Person[],
): StatusReport {
  return summarizeProjects(projectsOfTeam(teamId, projects, people));
}
